import type { ReactElement } from "react";
import {
  AttributeItem,
  AttributeList,
} from "@volca/ui/components/ui/attribute-list";
import {
  ObjectChip,
  ObjectChipLabel,
  ObjectPeek,
  ObjectPeekContent,
  ObjectPeekDescription,
  ObjectPeekHeader,
  ObjectPeekTitle,
  ObjectPeekTrigger,
} from "@volca/ui/components/ui/object-chip";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { isMissing } from "@/profitability/missing";
import { showValue } from "@/profitability/missing-value";
import { rate } from "@/profitability/review/labels";
import { useReviewSpec } from "@/profitability/review/review-spec";
import type { ReportColumn, ReportRow } from "@/profitability/types";
import { isLoss } from "./judgement";
import type { RecordObject } from "./objects";
import { recordTitle, rowMargin } from "./objects";
import { RecordMark } from "./record-mark";
import { formatCell, isNumeric } from "./table-values";

const PEEK_FIGURES = 4;

const canPreview = (
  object: Pick<RecordObject, "icon" | "name"> & Partial<RecordObject>,
): object is RecordObject =>
  object.dataset != null && object.nameColumn != null && object.plural != null;

/** The few figures a hover preview can repeat from the record's own page. */
const peekColumns = (object: RecordObject, row: ReportRow) => {
  const keys = new Set(object.dataset.key_columns ?? []);
  const rank = (column: ReportColumn) =>
    column.role === "total" ? 0 : column.role === "result" ? 1 : 2;
  return object.dataset.columns
    .filter(
      (column) =>
        !keys.has(column.key) &&
        column.key !== object.nameColumn.key &&
        !column.flag &&
        !column.references &&
        isNumeric(column) &&
        !isMissing(row[column.key]),
    )
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, PEEK_FIGURES);
};

/** One record by name, with its object's icon; a button when it opens. */
export const RecordChip = ({
  object,
  name,
  variant = "pill",
  size = "default",
  trade,
  mark = true,
  row,
  onOpen,
}: {
  object: Pick<RecordObject, "icon" | "name"> & Partial<RecordObject>;
  name: string;
  /** The business unit a job was done in, to draw its trade. */
  trade?: string | null;
  variant?: "pill" | "plain";
  size?: "sm" | "default";
  /** The record's mark before its name; off where the row already shows one. */
  mark?: boolean;
  /** The record's row, so hovering the chip previews its figures. */
  row?: ReportRow;
  onOpen?: () => void;
}) => {
  const chip = (
    <ObjectChip
      variant={variant}
      size={size}
      className={cn({
        "pointer-coarse:min-h-11 pointer-coarse:min-w-11": !!onOpen,
      })}
      render={
        onOpen ? (
          <button
            type="button"
            aria-label={`Open ${object.name.toLowerCase()} ${name}`}
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          />
        ) : undefined
      }
    >
      {mark && <RecordMark icon={object.icon} name={name} trade={trade} />}
      <ObjectChipLabel>{name}</ObjectChipLabel>
    </ObjectChip>
  );
  if (!row || !canPreview(object)) return chip;
  return (
    <RecordPeek object={object} name={name} row={row}>
      {chip}
    </RecordPeek>
  );
};

const RecordPeek = ({
  object,
  name,
  row,
  children,
}: {
  object: RecordObject;
  name: string;
  row: ReportRow;
  children: ReactElement;
}) => {
  const currency = useReportCurrency();
  const spec = useReviewSpec();
  const columns = peekColumns(object, row);
  const margin = columns.some((column) => column.type === "percent")
    ? null
    : rowMargin(object.dataset, row);
  return (
    <ObjectPeek>
      <ObjectPeekTrigger render={children} />
      <ObjectPeekContent>
        <ObjectPeekHeader>
          <ObjectPeekTitle>{recordTitle(object, row) || name}</ObjectPeekTitle>
          <ObjectPeekDescription>{object.name}</ObjectPeekDescription>
        </ObjectPeekHeader>
        {(columns.length > 0 || margin !== null) && (
          <AttributeList>
            {columns.map((column) => (
              <AttributeItem
                key={column.key}
                label={column.label ?? column.key}
              >
                <span
                  className={cn({
                    "text-danger": isLoss(column, row[column.key]),
                  })}
                >
                  {showValue(formatCell(row[column.key], column, currency))}
                </span>
              </AttributeItem>
            ))}
            {margin !== null && (
              <AttributeItem label={spec.metric.label}>
                {rate(margin)}
              </AttributeItem>
            )}
          </AttributeList>
        )}
      </ObjectPeekContent>
    </ObjectPeek>
  );
};
